import type { PropsWithChildren } from "react";
import { createContext, useMemo } from "react";
import type { Approval } from "../../../../api/types/approvalTypes";
import useClientContext from "../../../../context/useClientContext";
import useUserContext from "../../../../context/useUserContext";
import type { InvoiceActionEligibility } from "./invoiceGridActions";

interface InvoicesGridContextValue extends InvoiceActionEligibility {
  refetchPendingApprovals: () => void;
}

const InvoicesGridContext = createContext<InvoicesGridContextValue | undefined>(undefined);

interface Props {
  pendingApprovals: Approval[] | undefined;
  refetchPendingApprovals: () => void;
}

export const InvoicesGridContextProvider = ({
  pendingApprovals,
  refetchPendingApprovals,
  children,
}: PropsWithChildren<Props>) => {
  const { id: userId } = useUserContext();
  const { hasAnyPermission } = useClientContext();
  const canManageInvoices = hasAnyPermission(["ManageExpenses"]);

  const contextValue = useMemo(
    () => ({
      canManageInvoices,
      invoiceIdsPendingMyApproval: new Set(
        (pendingApprovals ?? []).flatMap((approval) =>
          approval.objectType === "Invoice" &&
          approval.objectId &&
          approval.status === "Pending" &&
          approval.pendingApprovalsFromUserIds.includes(userId)
            ? [approval.objectId]
            : []
        )
      ),
      refetchPendingApprovals,
    }),
    [canManageInvoices, pendingApprovals, refetchPendingApprovals, userId]
  );

  return <InvoicesGridContext value={contextValue}>{children}</InvoicesGridContext>;
};

export default InvoicesGridContext;
